/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AreaHighlight,
  Highlight,
  PdfHighlighter,
  PdfHighlighterUtils,
  PdfLoader,
  useHighlightContainerContext,
  usePdfHighlighterContext,
} from 'react-pdf-highlighter-extended';

import { TMaterializedCitationBlock } from '@/services/types/agent-chat-types';

import { CitationTimeline } from './CitationTimeline';

const HighlightContainer = () => {
  const {
    highlight,
    viewportToScaled,
    screenshot,
    isScrolledTo,
    highlightBindings,
  } = useHighlightContainerContext<Highlight>();

  const { toggleEditInProgress } = usePdfHighlighterContext();

  return (
    <div className="pointer-events-none">
      <AreaHighlight
        isScrolledTo={isScrolledTo}
        highlight={highlight}
        onChange={(boundingRect) => {
          const edit = {
            position: {
              boundingRect: viewportToScaled(boundingRect),
              rects: [],
            },
            content: {
              image: screenshot(boundingRect),
            },
          };

          toggleEditInProgress(false);
        }}
        bounds={highlightBindings.textLayer}
      />
    </div>
  );
};

export const CitationViewer = ({
  url,
  citations,
  selectedCitationId,
  setSelectedCitationId,
  shouldRender = true,
}: {
  url: string;
  citations: TMaterializedCitationBlock[];
  selectedCitationId: string;
  setSelectedCitationId: (id: string) => void;
  shouldRender?: boolean;
}) => {
  const highlighterUtilsRef = useRef<PdfHighlighterUtils>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(0);

  // Handler for navigating to a specific page
  const handlePageChange = useCallback(
    (targetPage: number) => {
      if (
        highlighterUtilsRef.current &&
        targetPage >= 1 &&
        targetPage <= pageCount
      ) {
        const viewer = highlighterUtilsRef.current.getViewer();
        if (viewer) {
          // Try to scroll to the target page
          // We'll create a synthetic highlight for the target page to scroll to
          const syntheticHighlight: Highlight = {
            id: `page-${targetPage}`,
            position: {
              boundingRect: {
                x1: 0,
                y1: 0,
                x2: 100,
                y2: 100,
                width: 100,
                height: 100,
                pageNumber: targetPage,
              },
              rects: [],
              usePdfCoordinates: false,
            },
            content: { text: `Page ${targetPage}` },
          };

          try {
            highlighterUtilsRef.current.scrollToHighlight(syntheticHighlight);
          } catch (error) {
            console.warn('Could not navigate to page:', error);
          }
        }
      }
    },
    [pageCount],
  );

  // Sort citations by page number and then by vertical position (y1)
  const sortedCitations = useMemo(() => {
    if (!shouldRender) return [];

    return [...citations].sort((a, b) => {
      // First sort by page number
      if (a.page_number !== b.page_number) {
        return a.page_number - b.page_number;
      }
      // Then sort by y1 position (vertical position)
      return a.bounding_box.y1 - b.bounding_box.y1;
    });
  }, [citations, shouldRender]);

  // Convert citations to highlights
  const citationHighlights = useMemo(() => {
    if (!shouldRender) return [];

    return sortedCitations.map(
      (citation): Highlight => ({
        id: citation.block_id,
        position: {
          boundingRect: {
            x1: citation.bounding_box.x1,
            y1: citation.bounding_box.y1,
            x2: citation.bounding_box.x2,
            y2: citation.bounding_box.y2,
            width: citation.page_dimensions.width,
            height: citation.page_dimensions.height,
            pageNumber: citation.page_number + 1,
          },
          rects: [],
          usePdfCoordinates: false,
        },
        // Placeholder content
        content: {
          // We can't easily get screenshots due to API limitations
          // Just provide descriptive text instead
          text: `Citation from page ${citation.page_number}`,
        },
      }),
    );
  }, [sortedCitations, shouldRender]);

  // Scroll to the selected highlight when the highlight index changes
  useEffect(() => {
    if (
      shouldRender &&
      highlighterUtilsRef.current &&
      citationHighlights.length > 0 &&
      selectedCitationId
    ) {
      const currentHighlight = citationHighlights.find(
        (highlight) => highlight.id === selectedCitationId,
      );
      const viewer = highlighterUtilsRef.current?.getViewer();
      if (
        currentHighlight &&
        viewer &&
        currentHighlight.position.boundingRect.pageNumber <= viewer.pagesCount
      ) {
        highlighterUtilsRef.current.scrollToHighlight(currentHighlight);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCitationId, citationHighlights, shouldRender]);

  useEffect(() => {
    if (!shouldRender) return undefined;

    const interval = setInterval(() => {
      // Your polling logic here
      const page = highlighterUtilsRef.current?.getViewer()?.currentPageNumber;
      const count = highlighterUtilsRef.current?.getViewer()?.pagesCount;
      if (page) {
        setCurrentPage(page);
        // Mark as initialized once we get the first page report from the viewer
        hasInitializedRef.current = true;
      }
      if (count) {
        setPageCount(count);
      }
    }, 150); // Polling interval in milliseconds

    return () => clearInterval(interval); // Cleanup on unmount
  }, [shouldRender]);

  // Track if the PDF viewer has initialized (reported its first page)
  const hasInitializedRef = useRef(false);

  // Deselect highlight when user scrolls away from its page
  useEffect(() => {
    // Don't deselect on initial mount - wait until PDF viewer has initialized
    if (!hasInitializedRef.current) return;
    if (!selectedCitationId) return;
    const hl = citationHighlights.find((h) => h.id === selectedCitationId);
    if (!hl) return;
    const highlightPage = hl.position.boundingRect.pageNumber;
    if (Math.abs(highlightPage - currentPage) > 2) {
      setSelectedCitationId('');
    }
  }, [
    currentPage,
    citationHighlights,
    selectedCitationId,
    setSelectedCitationId,
  ]);

  if (!shouldRender) {
    return (
      <div className="flex w-full h-full items-center justify-center text-gray-500">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-primary"
          aria-label="Loading"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full">
      {/* Top timeline bar */}
      <CitationTimeline
        highlights={citationHighlights}
        selectedHighlightId={selectedCitationId}
        setSelectedHighlightId={setSelectedCitationId}
        currentPage={currentPage}
        pageCount={pageCount}
        onPageChange={handlePageChange}
      />

      {/* PDF Viewer */}
      <div className="relative flex-1 min-h-0">
        <PdfLoader document={url}>
          {(pdfDocument) => (
            <PdfHighlighter
              pdfDocument={pdfDocument}
              highlights={citationHighlights}
              utilsRef={(_pdfHighlighterUtils) => {
                highlighterUtilsRef.current = _pdfHighlighterUtils;
              }}
            >
              <HighlightContainer />
            </PdfHighlighter>
          )}
        </PdfLoader>
      </div>
    </div>
  );
};
